import { ImagePaths } from '../../lib/imageUrls.js';

export const smbCybersecurityRiskAwarenessWorkshop = {

    id: 17,
    slug: 'smb-cybersecurity-risk-awareness-workshop',
    ogImage: '/og/courses/smb-cybersecurity-risk-awareness-workshop.jpg',
    courseCode: 'SMB-CYBER-RISK-001',
    title: 'SMB Cybersecurity Risk Assessment Workshop',
    subtitle: 'Hands-on risk assessment for small and medium businesses',
    valueProposition: 'Learn to identify your critical assets, understand vulnerabilities, and prioritise security investments through a structured, practical risk assessment process designed for SMB realities.',
    duration: '8 hours',
    students: 'New',
    level: 'Intermediate',
    image: ImagePaths.COURSE_3,
    shortDescription: 'SMB-CYBER-RISK-001 — Hands-on risk assessment workshop covering asset identification, threat analysis, and risk treatment for Australian SMB decision-makers.',
    price: {
      aud: 999,
      currency: 'AUD',
      earlyBird: 899
    },
    stripeConfig: {
    priceId: 'price_1T9bE8Ku1nyc322U8vF1712T',
    productId: 'prod_U7qwzrPrwYdHJU'
  },
    
    snapshot: {
      whoItsFor: [
        'SMB owners and directors',
        'IT managers and coordinators',
        'Business managers overseeing security',
        'Key decision-makers responsible for risk management'
      ],
      accreditation: 'Based on ISO 31000 risk management principles and ACSC guidance for SMBs'
    },
    
    logistics: {
      classSize: 'Maximum 15 participants for personalised guidance',
      requirements: [
        'Basic understanding of your business operations and systems',
        'Authority to make security investment decisions',
        'Understanding of cybersecurity concepts (or completion of SMB Cybersecurity Essentials)',
        'Laptop for working with assessment templates',
        'Information about your organisation\'s assets, systems, and data'
      ]
    },
    
    overview: 'This hands-on 8-hour workshop guides SMB owners, IT managers, and decision-makers through a practical, simplified cybersecurity risk assessment process. You\'ll learn to identify your business\' critical assets, understand potential vulnerabilities and threats, assess likelihood and impact, and prioritise security measures based on your specific needs and budget constraints. Using provided templates and tools, you\'ll conduct a real risk assessment for your own organisation during the workshop, leaving with a completed risk register and prioritised action plan. The workshop demystifies risk assessment—making it accessible without expensive consultants or complex frameworks—and ensures you invest security resources where they matter most for your business.',
    
    outcomes: [
      'Identify and catalogue your organisation\'s critical assets including data, systems, and infrastructure',
      'Assess threats and vulnerabilities relevant to your specific business context',
      'Evaluate likelihood and impact to determine risk levels',
      'Prioritise security controls and investments based on risk and budget',
      'Create a risk register and treatment plan for your organisation',
      'Apply simplified risk assessment methodologies suitable for SMBs',
      'Make informed security decisions using risk-based thinking',
      'Communicate security risks and investments to stakeholders and leadership'
    ],
    
    audience: [
      'SMB owners and company directors',
      'IT managers and systems coordinators',
      'Business managers overseeing security and compliance',
      'Operations managers responsible for risk management',
      'Finance professionals evaluating security investments',
      'Consultants and advisors supporting SMB clients'
    ],
    
    prerequisites: [
      'Understanding of your business operations, systems, and data',
      'Basic cybersecurity concepts (recommended: SMB Cybersecurity Essentials or equivalent)',
      'Authority to make or influence security decisions',
      'Access to information about your organisation\'s technology and assets'
    ],
    
    modules: [
      {
        title: 'Introduction to Risk Assessment for SMBs',
        duration: '1 hour',
        topics: [
          'Why risk assessment matters for small businesses',
          'Risk assessment principles: assets, threats, vulnerabilities, impact',
          'Simplified risk methodology for SMB contexts',
          'Common SMB security risks and priorities',
          'Overview of the risk assessment process',
          'Tools and templates you\'ll use'
        ]
      },
      {
        title: 'Identifying and Valuing Business Assets',
        duration: '1.5 hours',
        topics: [
          'What are assets? Data, systems, people, reputation',
          'Cataloguing your critical assets',
          'Understanding asset dependencies and interconnections',
          'Valuing assets: replacement cost, business impact, regulatory considerations',
          'Hands-on exercise: Building your asset inventory',
          'Classifying assets by criticality'
        ]
      },
      {
        title: 'Threat and Vulnerability Analysis',
        duration: '1.5 hours',
        topics: [
          'Common threats targeting your industry and business size',
          'Understanding vulnerabilities in your systems and processes',
          'Mapping threats to assets',
          'External threats: cybercriminals, competitors, hacktivists',
          'Internal threats: insider risks, accidental disclosure',
          'Hands-on exercise: Identifying threats and vulnerabilities for your business'
        ]
      },
      {
        title: 'Assessing Likelihood and Impact',
        duration: '1.5 hours',
        topics: [
          'Likelihood assessment: How probable is each threat?',
          'Impact assessment: What are the consequences?',
          'Using simplified risk matrices for SMBs',
          'Considering financial, operational, reputational, and legal impacts',
          'Risk scoring and categorisation',
          'Hands-on exercise: Scoring risks for your organisation'
        ]
      },
      {
        title: 'Risk Treatment and Prioritisation',
        duration: '1.5 hours',
        topics: [
          'Risk treatment options: mitigate, accept, transfer, avoid',
          'Selecting security controls appropriate to risk levels',
          'Cost-benefit analysis for SMB budgets',
          'Prioritising investments based on risk and resources',
          'Quick wins vs. long-term improvements',
          'Hands-on exercise: Building your risk treatment plan'
        ]
      },
      {
        title: 'Creating Your Risk Register and Action Plan',
        duration: '0.75 hours',
        topics: [
          'Documenting risks in a risk register',
          'Creating a practical, actionable security roadmap',
          'Assigning ownership and accountabilities',
          'Setting realistic timelines for implementation',
          'Communicating risks and plans to leadership and stakeholders',
          'Review and feedback on your completed risk assessment'
        ]
      },
      {
        title: 'Ongoing Risk Management',
        duration: '0.25 hours',
        topics: [
          'Updating risk assessments as your business changes',
          'Monitoring and reviewing implemented controls',
          'Responding to new threats and vulnerabilities',
          'Annual risk review processes',
          'Integrating risk thinking into business decisions'
        ]
      }
    ],
    
    format: {
      type: 'Live webinar or self-paced online with guided exercises',
      includes: [
        '8 hours of hands-on workshop instruction',
        'Risk assessment templates and tools (Excel, Word)',
        'Asset inventory and risk register templates',
        'Threat and vulnerability libraries specific to SMBs',
        'Risk matrix and scoring guides',
        'Security control selection frameworks',
        'Personalised guidance on your risk assessment (live sessions)',
        'Completed risk register for your organisation',
        'Access to materials for 12 months',
        'Certificate of completion'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Full-day live workshop (8 hours) or self-paced with guided exercises available immediately'
    },
    
    faqs: [
      {
        question: 'Do we need prior risk assessment experience?',
        answer: 'No prior experience required. The workshop uses simplified, practical methods designed specifically for SMBs without dedicated risk management expertise. We guide you step-by-step through the process using templates and examples.'
      },
      {
        question: 'Will we complete a risk assessment for our business during the workshop?',
        answer: 'Yes! The workshop is hands-on—you\'ll work through the risk assessment process for your own organisation and leave with a completed risk register and action plan. You\'ll receive guidance and feedback along the way.'
      },
      {
        question: 'What information do we need to bring about our business?',
        answer: 'You\'ll need general information about your systems, data, operations, and technology environment. We provide an information gathering checklist before the workshop. You don\'t need detailed technical documentation—a working understanding of your business operations is sufficient.'
      },
      {
        question: 'How is this different from hiring a consultant to do a risk assessment?',
        answer: 'This workshop teaches you to conduct risk assessments yourself, empowering ongoing risk management without ongoing consultant costs. You\'ll build internal capability and understanding. Many participants engage consultants for specific technical assessments later but manage overall risk themselves.'
      },
      {
        question: 'Will this help us prioritise our cybersecurity budget?',
        answer: 'Yes—that\'s a key outcome. You\'ll learn to prioritise security investments based on your specific risks and business context, ensuring you allocate resources to the controls that matter most. You\'ll leave with a clear, prioritised security roadmap.'
      },
      {
        question: 'Can multiple people from our organisation attend?',
        answer: 'Yes, and we encourage it! Having IT staff, management, and finance representatives working together creates a more comprehensive assessment. For teams of 3 or more, contact us for a private quote.'
      },
      {
        question: 'Does this cover compliance requirements like ISO 27001 or Essential Eight?',
        answer: 'While not a certification course, the risk assessment process aligns with ISO 31000 risk management principles and supports Essential Eight, ISO 27001, and Australian Privacy Principles compliance. You\'ll understand how to assess risks in the context of these frameworks.'
      },
      {
        question: 'What ongoing support is provided after the workshop?',
        answer: 'You\'ll have 12 months of access to all templates, tools, and materials. We provide email support for 60 days to answer questions as you refine and implement your risk assessment. You can also re-attend future workshops to update your assessment.'
      }
    ]
};
